import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import {
  listWarehouses,
  createWarehouse,
  updateWarehouse,
  updateWarehouseStatus,
  isWarehouseError,
} from "@/infrastructure/warehouse/warehouseService";
import { isPlanLimitError } from "@/infrastructure/errors/planLimitError";
import { listTypeUses } from "@/infrastructure/type-use/typeUseService";
import type { WarehouseRecord, WarehouseErrorCode, TypeUse } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { DropdownSelector, Tooltip } from "@/design-system";
import { SectionGrid } from "./components/SectionGrid";
import { UsagePanel } from "./components/UsagePanel";
import { WarehouseFormModal } from "./components/WarehouseFormModal";
import { PlanLimitModal } from "../components/PlanLimitModal";
import { SiigoModal } from "../inventory/components/SiigoModal";
import { SiigoIcon } from "../inventory/components/SiigoIcon";

export const WarehousesPage = memo(function WarehousesPage({
  onNavigateTypeUse,
  onNavigateUsage,
}: {
  onNavigateTypeUse: () => void;
  onNavigateUsage: () => void;
}) {
  const { t } = useI18n();
  const { canCreateWarehouse, canEditWarehouse } = useRole();
  const { isAtLimit, refresh: refreshUsage } = useTenantUsage();
  const warehouseAtLimit = isAtLimit("warehouses");
  const [items, setItems] = useState<WarehouseRecord[]>([]);
  const [typeUses, setTypeUses] = useState<TypeUse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeIsleId, setActiveIsleId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<WarehouseRecord | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [planLimitMessage, setPlanLimitMessage] = useState<string | null>(null);
  const [showSiigo, setShowSiigo] = useState(false);

  const hasTypeUses = typeUses.length > 0;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [warehouseData, typeUseData] = await Promise.all([listWarehouses(), listTypeUses()]);
      setItems(warehouseData);
      setTypeUses(typeUseData);
      if (warehouseData.length > 0 && !activeId) setActiveId(warehouseData[0]!.warehouse_id);
    } catch {}
    setLoading(false);
  }, [activeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getErrorMessage = useCallback(
    (code: WarehouseErrorCode): string => {
      const map: Record<WarehouseErrorCode, string> = {
        DUPLICATE_CODE: t.warehouses.errorDuplicateCode,
        VALIDATION: t.warehouses.errorValidation,
        NOT_FOUND: t.warehouses.errorNotFound,
        FORBIDDEN: t.warehouses.errorForbidden,
        UNAUTHORIZED: t.warehouses.errorForbidden,
        UNKNOWN: t.warehouses.errorForbidden,
      };
      return map[code];
    },
    [t.warehouses]
  );

  const handleCreate = useCallback(
    async (data: { name: string; code: string; description: string; typeUseId: string }) => {
      try {
        await createWarehouse(data);
        showToast(t.warehouses.successCreated, "success");
        setShowForm(false);
        fetchData();
        refreshUsage();
      } catch (err) {
        if (isPlanLimitError(err)) { setPlanLimitMessage(err.backendMessage); return; }
        if (isWarehouseError(err)) showToast(getErrorMessage(err.message as WarehouseErrorCode), "error");
      }
    },
    [fetchData, showToast, t.warehouses.successCreated, getErrorMessage, refreshUsage]
  );

  const handleUpdate = useCallback(
    async (data: { name: string; code: string; description: string; typeUseId: string }) => {
      if (!editingItem) return;
      try {
        await updateWarehouse({ id: editingItem.warehouse_id, name: data.name, description: data.description, typeUseId: data.typeUseId });
        showToast(t.warehouses.successUpdated, "success");
        setEditingItem(null);
        setShowForm(false);
        fetchData();
      } catch (err) {
        if (isWarehouseError(err)) showToast(getErrorMessage(err.message as WarehouseErrorCode), "error");
      }
    },
    [editingItem, fetchData, showToast, t.warehouses.successUpdated, getErrorMessage]
  );

  const handleToggleStatus = useCallback(
    async (item: WarehouseRecord) => {
      const newState = item.warehouse_state === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      try {
        await updateWarehouseStatus({ id: item.warehouse_id, status: newState });
        showToast(t.warehouses.successStatusChanged, "success");
        fetchData();
        refreshUsage();
      } catch (err) {
        if (isWarehouseError(err)) showToast(getErrorMessage(err.message as WarehouseErrorCode), "error");
      }
    },
    [fetchData, showToast, t.warehouses.successStatusChanged, getErrorMessage, refreshUsage]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div className="platform-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>
          {t.warehouses.title} ({items.length})
        </h1>
        <div style={{ display: "flex", gap: "8px" }}>
          {canCreateWarehouse && (
            <button onClick={() => setShowSiigo(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#555", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `all ${transitions.fast}` }} onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f9f9f9"; }} onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
              <SiigoIcon size={16} /> Siigo
            </button>
          )}
          {hasTypeUses && canCreateWarehouse && (
            <Tooltip content={t.planLimit.tooltipDisabled} disabled={!warehouseAtLimit}>
              <button
                onClick={() => { setShowForm(true); setEditingItem(null); }}
                disabled={warehouseAtLimit}
                style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: warehouseAtLimit ? "#ccc" : colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: warehouseAtLimit ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}`, opacity: warehouseAtLimit ? 0.7 : 1 }}
                onMouseEnter={e => { if (!warehouseAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover; }}
                onMouseLeave={e => { if (!warehouseAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primary; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                {t.warehouses.addWarehouse}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {loading && <LoadingState />}

      {!loading && !hasTypeUses && <NoTypeUseState onNavigate={onNavigateTypeUse} />}

      {!loading && hasTypeUses && items.length === 0 && <EmptyState />}

      {!loading && hasTypeUses && items.length > 0 && (
        <>
          <DropdownSelector
            items={items.map(w => ({ id: w.warehouse_id, name: w.warehouse_name }))}
            activeId={activeId}
            onSelect={setActiveId}
          />
          <ActiveWarehouseBar
            item={items.find(w => w.warehouse_id === activeId) ?? items[0]!}
            onEdit={(item) => { setEditingItem(item); setShowForm(true); }}
            onToggleStatus={handleToggleStatus}
          />
          <div className="warehouse-detail-grid" style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", padding: "28px", display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
            {activeId && <SectionGrid warehouseId={activeId} onIsleChange={setActiveIsleId} onNavigateUsage={onNavigateUsage} />}
            <UsagePanel warehouseId={activeId ?? undefined} isleId={activeIsleId ?? undefined} />
          </div>
        </>
      )}

      {showForm && (
        <WarehouseFormModal
          item={editingItem}
          onSave={editingItem ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} />}
      {planLimitMessage && (
        <PlanLimitModal
          message={planLimitMessage}
          onClose={() => setPlanLimitMessage(null)}
          onViewPlan={() => { setPlanLimitMessage(null); onNavigateUsage(); }}
        />
      )}
      {showSiigo && (
        <SiigoModal
          configs={[{ entity: "WAREHOUSES", statusType: "sync_warehouses", title: "Bodegas", subtitle: "Sincroniza bodegas de Siigo con Nexo Warehouse." }]}
          onClose={() => setShowSiigo(false)}
        />
      )}
    </div>
  );
});

const ActiveWarehouseBar = memo(function ActiveWarehouseBar({
  item, onEdit, onToggleStatus,
}: {
  item: WarehouseRecord;
  onEdit: (item: WarehouseRecord) => void;
  onToggleStatus: (item: WarehouseRecord) => void;
}) {
  const { t } = useI18n();
  const { canEditWarehouse } = useRole();
  const isActive = item.warehouse_state === "ACTIVE";

  return (
    <div
      className="warehouse-bar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e8e8e8",
        padding: "12px 20px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>
            {item.warehouse_name}
          </span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>
            {item.warehouse_code} · {item.type_use?.name ?? "—"}
          </span>
        </div>
        <span
          style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)",
            color: isActive ? "#16a34a" : "#6b7280",
            fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold,
            padding: "4px 10px", borderRadius: "9999px",
          }}
        >
          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} aria-hidden="true" />
          {isActive ? t.warehouses.statusActive : t.warehouses.statusInactive}
        </span>
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        {canEditWarehouse && <ActionBtn label={t.warehouses.editWarehouse} onClick={() => onEdit(item)} />}
        {canEditWarehouse && <ActionBtn
          label={isActive ? t.warehouses.deactivate : t.warehouses.activate}
          onClick={() => onToggleStatus(item)}
          danger={isActive}
        />}
      </div>
    </div>
  );
});

function ActionBtn({ label, onClick, danger = false }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold,
        color: danger ? colors.error : "#555", background: "transparent",
        border: `1px solid ${danger ? "rgba(239,68,68,0.3)" : "#e0e0e0"}`,
        borderRadius: "8px", padding: "7px 14px", cursor: "pointer",
        transition: `all ${transitions.fast}`, whiteSpace: "nowrap",
      }}
      onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? colors.error : "#111"; el.style.color = danger ? colors.error : "#111"; }}
      onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? "rgba(239,68,68,0.3)" : "#e0e0e0"; el.style.color = danger ? colors.error : "#555"; }}
    >
      {label}
    </button>
  );
}

function NoTypeUseState({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useI18n();
  return (
    <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", padding: "64px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", textAlign: "center" }}>
      <div style={{ width: "52px", height: "52px", background: "rgba(245,158,11,0.1)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", color: "#f59e0b" }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
      </div>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#333", margin: 0 }}>{t.warehouses.emptyNoTypeUseTitle}</p>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: 0, maxWidth: "360px" }}>{t.warehouses.emptyNoTypeUseSubtitle}</p>
      <button
        onClick={onNavigate}
        style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: colors.primary, border: "none", borderRadius: "9px", padding: "10px 24px", cursor: "pointer", marginTop: "8px", transition: `background ${transitions.fast}` }}
        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)}
        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}
      >
        {t.warehouses.emptyNoTypeUseAction}
      </button>
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", padding: "64px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center" }}>
      <div style={{ width: "48px", height: "48px", background: "#f5f5f5", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
      </div>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.warehouses.emptyTitle}</p>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb", margin: 0, maxWidth: "300px" }}>{t.warehouses.emptySubtitle}</p>
    </div>
  );
}

function Toast({ message, type }: { message: string; type: "success" | "error" }) {
  return (
    <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300, animation: "fadeInUp 0.3s ease" }}>
      {message}
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}>
      <span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
    </div>
  );
}
